import React, { useState, useEffect } from 'react';
import { DialogShell } from './ui/DialogShell';
import { X, UserX, RefreshCw, ChevronLeft, ChevronRight, Calendar, FileText } from 'lucide-react';
import { useLumisial } from '../context/LumisialContext';
import { useAuth } from '../context/AuthContext';
import { miembroService, PaginatedMiembrosResponse } from '../services/miembroService';
import { Miembro } from '../lib/supabase';
import { ReingresarMiembroModal } from './ReingresarMiembroModal';

interface MiembrosSinDerechosModalProps {
  isOpen: boolean;
  onClose: () => void;
  onMiembroReingresado?: () => void;
}

export const MiembrosSinDerechosModal: React.FC<MiembrosSinDerechosModalProps> = ({
  isOpen,
  onClose,
  onMiembroReingresado
}) => {
  const { selectedLumisial } = useLumisial();
  const { usuario } = useAuth();
  const [response, setResponse] = useState<PaginatedMiembrosResponse>({
    data: [],
    total: 0,
    totalAdultos: 0,
    totalNinos: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0
  });
  const [loading, setLoading] = useState(false);
  const [selectedMiembro, setSelectedMiembro] = useState<Miembro | null>(null);
  const [showReingresarModal, setShowReingresarModal] = useState(false);

  useEffect(() => {
    if (isOpen && selectedLumisial) {
      loadMiembrosSinDerechos(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedLumisial]);

  const loadMiembrosSinDerechos = async (page: number) => {
    if (!selectedLumisial) return;

    try {
      setLoading(true);
      const data = await miembroService.getMiembrosSinDerechos(
        selectedLumisial.id,
        page,
        10
      );
      setResponse(data);
    } catch (error) {
      console.error('Error loading miembros sin derechos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReingresar = (miembro: Miembro) => {
    setSelectedMiembro(miembro);
    setShowReingresarModal(true);
  };

  const handleConfirmReingresar = async () => {
    if (!selectedMiembro || !usuario) return;

    try {
      await miembroService.reingresarMiembro(selectedMiembro.id, usuario.id);
      await loadMiembrosSinDerechos(response.page);
      if (onMiembroReingresado) {
        onMiembroReingresado();
      }
      setShowReingresarModal(false);
      setSelectedMiembro(null);
    } catch (error) {
      console.error('Error al reingresar miembro:', error);
      throw error;
    }
  };

  const formatDate = (date?: string) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getIngresoLabel = (num: number) => {
    if (num === 1) return 'Primer ingreso';
    if (num === 2) return 'Segundo ingreso';
    if (num === 3) return 'Tercer ingreso';
    return `${num}° ingreso`;
  };

  if (!isOpen) return null;

  const renderMiembros = () => {
    if (loading) {
      return (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-700"></div>
        </div>
      );
    }
    if (response.data.length === 0) {
      return (
        <div className="text-center py-12">
          <UserX className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600 text-lg font-medium">No hay miembros sin derechos</p>
          <p className="text-slate-500 text-sm mt-2">
            Todos los miembros de este lumisial están activos
          </p>
        </div>
      );
    }
    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b-2 border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Nombre</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">RUT/Pasaporte</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Edad</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Fecha Cambio</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Motivo</th>
              <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Ingresos</th>
              <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {response.data.map((miembro) => (
              <tr key={miembro.id} className="hover:bg-slate-50 transition-colors">
                <td className="px-4 py-4">
                  <div>
                    <p className="text-sm font-medium text-slate-800">
                      {miembro.nombres} {miembro.apellidos}
                    </p>
                    {miembro.email && (
                      <p className="text-xs text-slate-500">{miembro.email}</p>
                    )}
                  </div>
                </td>
                <td className="px-4 py-4 text-sm text-slate-700">
                  {miembro.rut_pasaporte || '-'}
                </td>
                <td className="px-4 py-4">
                  <span className="text-xs bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full font-medium">
                    {miembro.tipo_miembro === 'adulto' ? 'Adulto' : 'Niño'}
                  </span>
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    {formatDate(miembro.estado_cambiado_fecha)}
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-start gap-2 max-w-xs">
                    <FileText className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-slate-600 line-clamp-2">
                      {miembro.motivo_cambio_estado || 'Sin motivo especificado'}
                    </p>
                  </div>
                </td>
                <td className="px-4 py-4 text-center">
                  <span className="text-sm font-semibold text-slate-700">
                    {miembro.numero_ingreso}
                  </span>
                  <p className="text-xs text-slate-500">
                    {getIngresoLabel(miembro.numero_ingreso)}
                  </p>
                </td>
                <td className="px-4 py-4 text-center">
                  <button
                    onClick={() => handleReingresar(miembro)}
                    className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 transition-colors"
                    title="Reingresar miembro"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Reingresar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <>
      <DialogShell
        isOpen={isOpen}
        onClose={onClose}
        size="xl"
        header={(
          <div className="sticky top-0 z-10 bg-gradient-to-r from-slate-700 to-slate-800 text-white p-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <UserX className="w-6 h-6" />
              <div>
                <h2 className="text-2xl font-bold">Miembros Sin Derechos</h2>
                <p className="text-slate-300 text-sm mt-1">
                  {response.total} miembro{response.total !== 1 ? 's' : ''} sin derechos actualmente
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        )}
      >
        {renderMiembros()}

        {response.totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-6 border-t border-slate-200">
            <div className="text-sm text-slate-600">
              Página {response.page} de {response.totalPages} ({response.total} miembros)
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => loadMiembrosSinDerechos(response.page - 1)}
                disabled={response.page === 1 || loading}
                className="flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5" />
                Anterior
              </button>
              <button
                onClick={() => loadMiembrosSinDerechos(response.page + 1)}
                disabled={response.page === response.totalPages || loading}
                className="flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Siguiente
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        <div className="-mx-6 -mb-6 mt-6 border-t border-slate-200 p-6 bg-slate-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-slate-600">
              <p>
                <span className="font-semibold">{response.totalAdultos}</span> adultos •{' '}
                <span className="font-semibold">{response.totalNinos}</span> niños
              </p>
            </div>
            <button
              onClick={onClose}
              className="px-6 py-3 bg-slate-600 text-white font-semibold rounded-lg hover:bg-slate-700 transition-colors"
            >
              Cerrar
            </button>
          </div>
        </div>
      </DialogShell>

      <ReingresarMiembroModal
        isOpen={showReingresarModal}
        onClose={() => {
          setShowReingresarModal(false);
          setSelectedMiembro(null);
        }}
        onConfirm={handleConfirmReingresar}
        miembro={selectedMiembro}
      />
    </>
  );
};
